import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { KPI, Panel, EmptyRow } from "@/components/DashboardWidgets";
import { Button } from "@/components/ui/button";
import {
  Users, UserCheck, UserPlus, ClipboardCheck, UserX, Clock, Wallet, AlertCircle,
  BookOpen, Calendar, Bus, Library, GraduationCap, FileText, Bell, Plus, MessageSquare,
} from "lucide-react";

export default function SchoolDashboard() {
  const [s, setS] = useState({});
  const [holidays, setHolidays] = useState([]);
  useEffect(() => {
    api.get("/stats/school-full").then((r) => setS(r.data)).catch(() => {});
    api.get("/erp/holidays?upcoming=true").then((r) => setHolidays(r.data)).catch(() => {});
  }, []);

  const attendanceRate = s.total_students ? Math.round((s.today_present || 0) * 100 / s.total_students) : 0;

  return (
    <DashLayout variant="school" title="Admin Dashboard">
      {/* 16 KPI Widgets */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Users} label="Total Students" value={s.total_students} color="text-blue-700 bg-blue-50" testId="kpi-total-students" />
        <KPI icon={UserCheck} label="Active Students" value={s.active_students} color="text-emerald-700 bg-emerald-50" testId="kpi-active" />
        <KPI icon={UserPlus} label="New Admissions" value={s.new_admissions} sub="30d" color="text-purple-700 bg-purple-50" testId="kpi-new" />
        <KPI icon={ClipboardCheck} label="Today Attendance" value={`${attendanceRate}%`} sub={`${s.today_present || 0} present`} color="text-teal-700 bg-teal-50" testId="kpi-today-att" />
        <KPI icon={UserX} label="Absent Today" value={s.today_absent} color="text-red-700 bg-red-50" testId="kpi-absent" />
        <KPI icon={Clock} label="Late Today" value={s.today_late} color="text-amber-700 bg-amber-50" testId="kpi-late" />
        <KPI icon={Wallet} label="Today's Fees" value={`₹${(s.todays_fees_collection || 0).toLocaleString()}`} color="text-emerald-700 bg-emerald-50" testId="kpi-fees-today" />
        <KPI icon={AlertCircle} label="Pending Fees" value={`₹${(s.total_pending_fees || 0).toLocaleString()}`} color="text-orange-700 bg-orange-50" testId="kpi-fees-pending" />
        <KPI icon={BookOpen} label="Upcoming Exams" value={(s.upcoming_exams || []).length} color="text-indigo-700 bg-indigo-50" testId="kpi-exams" />
        <KPI icon={Calendar} label="Upcoming Events" value={(s.upcoming_events || []).length} color="text-pink-700 bg-pink-50" testId="kpi-events" />
        <KPI icon={Bus} label="Bus Routes" value={s.bus_routes} color="text-yellow-700 bg-yellow-50" testId="kpi-bus" />
        <KPI icon={Library} label="Library Due" value={s.library_due ?? "—"} sub="soon" color="text-slate-700 bg-slate-100" testId="kpi-library" />
        <KPI icon={GraduationCap} label="Teacher Attendance" value={`${s.teacher_present || 0}/${s.teacher_total || 0}`} color="text-cyan-700 bg-cyan-50" testId="kpi-teach-att" />
        <KPI icon={FileText} label="Pending Docs" value={s.pending_documents} color="text-rose-700 bg-rose-50" testId="kpi-docs" />
        <KPI icon={Bell} label="New Enquiries" value={s.pending_enquiries} color="text-blue-700 bg-blue-50" testId="kpi-enq" />
        <KPI icon={MessageSquare} label="Pending Leaves" value={s.pending_leaves} color="text-purple-700 bg-purple-50" testId="kpi-leaves" />
      </div>

      {/* Quick Actions */}
      <Panel title="Quick Actions" testId="quick-actions">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {[
            ["/school/admissions", "New Admission", UserPlus],
            ["/school/attendance", "Mark Attendance", ClipboardCheck],
            ["/school/fees", "Record Fee", Wallet],
            ["/school/exams", "Add Exam", BookOpen],
            ["/school/messaging", "Send Notice", MessageSquare],
            ["/school/enquiries", "View Enquiries", Bell],
          ].map(([to, label, Icon]) => (
            <Link key={to} to={to}>
              <Button variant="outline" className="w-full justify-start gap-2 text-xs" data-testid={`qa-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="w-4 h-4" /> {label}
              </Button>
            </Link>
          ))}
        </div>
      </Panel>

      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <Panel title="Upcoming Holidays" testId="panel-holidays">
          {holidays.length === 0 ? <EmptyRow text="No upcoming holidays" /> : (
            <ul className="space-y-2">
              {holidays.slice(0, 6).map((h) => (
                <li key={h.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <div><div className="font-semibold">{h.name}</div><div className="text-xs text-slate-500">{h.scope}</div></div>
                  <div className="text-xs text-slate-500 font-mono">{h.date}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Upcoming Exams" testId="panel-exams" action={<Link to="/school/exams" className="text-xs text-blue-700 hover:underline">View all →</Link>}>
          {(s.upcoming_exams || []).length === 0 ? <EmptyRow text="No upcoming exams" /> : (
            <ul className="space-y-2">
              {s.upcoming_exams.map((e) => (
                <li key={e.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <div><div className="font-semibold">{e.name}</div><div className="text-xs text-slate-500">Class {e.class_name} · {e.subject}</div></div>
                  <div className="text-xs text-slate-500 font-mono">{e.exam_date}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Upcoming Events" testId="panel-events" action={<Link to="/super/events" className="text-xs text-blue-700 hover:underline">View all →</Link>}>
          {(s.upcoming_events || []).length === 0 ? <EmptyRow text="No upcoming events" /> : (
            <ul className="space-y-2">
              {s.upcoming_events.map((e) => (
                <li key={e.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <div><div className="font-semibold">{e.title}</div><div className="text-xs text-slate-500">{e.city || "—"}</div></div>
                  <div className="text-xs text-slate-500 font-mono">{e.event_date}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </DashLayout>
  );
}
